import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { produce } from "immer";
import { ReactNode } from "react";

export type BreadcrumbItem = {
  title: string;
  href?: string;
  isActive?: boolean;
};

interface PageInfo {
  path: string;
  locale: string;
  title?: string;
}

interface SidePanel {
  content: ReactNode | null;
  isVisible: boolean;
  width?: number;
}

interface PageMeta {
  title: string;
  description: string;
}

interface PageLayoutStoreState {
  // Page info
  page: PageInfo | null;
  setPage: (page: PageInfo) => void;
  clearPage: () => void;

  // Breadcrumbs
  breadcrumbs: BreadcrumbItem[];
  setBreadcrumbs: (breadcrumbs: BreadcrumbItem[]) => void;
  clearBreadcrumbs: () => void;

  // Side panels (2 panels: left and right)
  leftSidePanel: SidePanel;
  rightSidePanel: SidePanel;
  setLeftSidePanel: (panel: Partial<SidePanel>) => void;
  setRightSidePanel: (panel: Partial<SidePanel>) => void;
  toggleLeftSidePanel: () => void;
  toggleRightSidePanel: () => void;
  showLeftSidePanel: () => void;
  showRightSidePanel: () => void;
  hideLeftSidePanel: () => void;
  hideRightSidePanel: () => void;
  clearLeftSidePanel: () => void;
  clearRightSidePanel: () => void;

  // Meta (SEO)
  meta: PageMeta | null;
  setMeta: (meta: PageMeta) => void;
  clearMeta: () => void;
}

const defaultSidePanel: SidePanel = {
  content: null,
  isVisible: false,
  width: 320,
};

const useLayoutStore = create(
  devtools<PageLayoutStoreState>((set, get) => {
    const update = (updater: (state: PageLayoutStoreState) => void) =>
      set(produce(updater));

    return {
      // Page
      page: null,
      setPage: (page: PageInfo) => {
        update((state) => {
          state.page = page;
        });
      },
      clearPage: () => {
        update((state) => {
          state.page = null;
        });
      },

      // Breadcrumbs
      breadcrumbs: [],
      setBreadcrumbs: (breadcrumbs: BreadcrumbItem[]) => {
        update((state) => {
          state.breadcrumbs = breadcrumbs;
        });
      },
      clearBreadcrumbs: () => {
        update((state) => {
          state.breadcrumbs = [];
        });
      },

      // Side panels
      leftSidePanel: { ...defaultSidePanel },
      rightSidePanel: { ...defaultSidePanel },
      setLeftSidePanel: (panel: Partial<SidePanel>) => {
        update((state) => {
          state.leftSidePanel = {
            ...state.leftSidePanel,
            ...panel,
          };
        });
      },
      setRightSidePanel: (panel: Partial<SidePanel>) => {
        update((state) => {
          state.rightSidePanel = {
            ...state.rightSidePanel,
            ...panel,
          };
        });
      },
      toggleLeftSidePanel: () => {
        update((state) => {
          state.leftSidePanel.isVisible = !state.leftSidePanel.isVisible;
        });
      },
      toggleRightSidePanel: () => {
        update((state) => {
          state.rightSidePanel.isVisible = !state.rightSidePanel.isVisible;
        });
      },
      showLeftSidePanel: () => {
        update((state) => {
          state.leftSidePanel.isVisible = true;
        });
      },
      showRightSidePanel: () => {
        update((state) => {
          state.rightSidePanel.isVisible = true;
        });
      },
      hideLeftSidePanel: () => {
        update((state) => {
          state.leftSidePanel.isVisible = false;
        });
      },
      hideRightSidePanel: () => {
        update((state) => {
          state.rightSidePanel.isVisible = false;
        });
      },
      clearLeftSidePanel: () => {
        update((state) => {
          state.leftSidePanel = { ...defaultSidePanel };
        });
      },
      clearRightSidePanel: () => {
        update((state) => {
          state.rightSidePanel = { ...defaultSidePanel };
        });
      },

      // Meta
      meta: null,
      setMeta: (meta: PageMeta) => {
        update((state) => {
          state.meta = meta;
        });
      },
      clearMeta: () => {
        update((state) => {
          state.meta = null;
        });
      },
    };
  })
);

export default useLayoutStore;
